/**
 * Starting VMs through the privileged callable, with the failure turned into
 * something an operator can read — and, for a stockout, act on.
 *
 * One implementation because there are two callers with the same obligation and
 * different shapes: the host card's Start button (one machine, named) and the
 * release flow's Wake (both machines, one press). A callable failure — permission,
 * an unprovisioned VM, a Compute error — is the operator's problem to act on, and
 * a swallowed one looks exactly like a dead button, so neither caller is allowed
 * to be the one that forgets.
 *
 * A failure is a value rather than an exception here for the same reason: the
 * callers render it beside the button they pressed, and there is no layer above
 * them that would know what to do with a throw.
 */
import { FirebaseError } from 'firebase/app';
import { isHostStartStockout, type HostRole, type HostStartRequest, type HostStartStockout } from '@finvari/deployer';
import { startHost } from '../firebase';

/**
 * A start that did not happen. `stockout` is present only when the zone is out of
 * capacity for the shape that was tried, which is the one failure the page can
 * offer a way past — the others are for reading.
 */
export interface StartFailure {
    message: string;
    stockout?: HostStartStockout;
}

/**
 * Start one VM, resolving to the failure to show or `undefined` on success.
 *
 * With no `machineType` or `tryAllChoices` the VM starts on whatever shape it is
 * already on, which is every start before an operator has had reason to pick.
 */
export async function startHostReportingError(
    role: HostRole,
    options: Omit<HostStartRequest, 'host'> = {},
): Promise<StartFailure | undefined> {
    try {
        await startHost({ host: role, ...options });
        return undefined;
    } catch (err) {
        return asStartFailure(err);
    }
}

/** Start on one of the host's offered machine types, resizing the stopped VM first. */
export function startHostOn(role: HostRole, machineType: string): Promise<StartFailure | undefined> {
    return startHostReportingError(role, { machineType });
}

/** Walk the host's machine types in order and start on the first with capacity. */
export function startHostTryingAll(role: HostRole): Promise<StartFailure | undefined> {
    return startHostReportingError(role, { tryAllChoices: true });
}

/**
 * The callable's error as something to render. `details` survives the callable
 * boundary intact, which is what lets the stockout carry the shapes it tried
 * rather than the UI parsing them back out of the sentence.
 */
function asStartFailure(err: unknown): StartFailure {
    const message = err instanceof Error ? err.message : String(err);
    const details = err instanceof FirebaseError ? (err as FirebaseError & { details?: unknown }).details : undefined;
    return isHostStartStockout(details) ? { message, stockout: details } : { message };
}

export interface WakeFailure extends StartFailure {
    role: HostRole;
}

/**
 * Start several VMs, reporting each failure against the machine it happened on.
 *
 * Sequential rather than concurrent: there are two hosts and one Compute call
 * each, so there is nothing to gain from overlapping them, and a serial run keeps
 * the failures in a predictable order.
 */
export async function wakeHosts(roles: HostRole[]): Promise<WakeFailure[]> {
    const failures: WakeFailure[] = [];
    for (const role of roles) {
        const failure = await startHostReportingError(role);
        if (failure) failures.push({ role, ...failure });
    }
    return failures;
}
